import * as THREE from 'three';
import type { SketchSplineObject } from '../../../mesh/sketch/SketchSplineObject';
import type { ProjectionResult } from '../model/ProjectionResult';
import type {
	ReferenceDrivenSplineRelationRuntimeRecord,
	SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import { resolveSourceRefEntityId } from '../model/SketchEntityReferenceRecord';
import type { ExecutionDiagnostic } from '../model/SketchEntityReferenceDiagnostics';
import { BaseReferenceDrivenRelationService } from './BaseReferenceDrivenRelationService';
import { ReferenceDrivenSplineOnCurveConstraint } from './constraints/ReferenceDrivenSplineOnCurveConstraint';
import type {
	ReferenceDrivenSplineRelationBinding,
	ReferenceDrivenSplineRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/ReferenceDrivenSplineRelationService';

export type {
	ReferenceDrivenSplineRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/ReferenceDrivenSplineRelationService';

/**
 * 参考驱动普通样条关系服务。
 */
export class ReferenceDrivenSplineRelationService
	extends BaseReferenceDrivenRelationService<
		ReferenceDrivenSplineRelationRuntimeRecord,
		ReferenceDrivenSplineRelationBinding
	> {
	/**
	 * 挂载普通样条 on-curve 约束。
	 *
	 * @param input 挂载输入。
	 * @returns 挂载结果。
	 */
	public attach(input: {
		reference: SketchEntityReferenceRecord;
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'spline' }>;
		targetSpline: SketchSplineObject;
		runtimeSourceName?: string;
	}): ReferenceDrivenSplineRelationAttachResult {
		this.detach({
			referenceId: input.reference.referenceId,
			targetSpline: input.targetSpline,
		});

		const record: ReferenceDrivenSplineRelationRuntimeRecord = {
			referenceId: input.reference.referenceId,
			relationKind: 'on-curve',
			driveMode: 'projection-drives-target',
			sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
			targetEntityId: input.targetSpline.persistentId,
			constraintId: -1,
		};
		const constraint = new ReferenceDrivenSplineOnCurveConstraint({
			referenceId: input.reference.referenceId,
			projectedControlPointWorlds: input.projectionResult.geometryPayload.controlPointWorlds
				.map((point) => new THREE.Vector3(...point)),
			projectedCurvePointWorlds: input.projectionResult.geometryPayload.curvePointWorlds
				.map((point) => new THREE.Vector3(...point)),
			projectedCurveMetadata: input.projectionResult.geometryPayload.curveMetadata,
			// 关系公式必须消费与目标几何同源的极点定义，禁止在求解阶段由显示折线二次拟合。
			...(input.projectionResult.geometryPayload.curveEvaluationDefinition
				? {
					projectedCurveEvaluationDefinition:
						input.projectionResult.geometryPayload.curveEvaluationDefinition,
				}
				: {}),
			sourceDisplayLabel: input.reference.sourceDisplayLabel,
			runtimeSourceName: input.runtimeSourceName,
		});
		input.targetSpline.constraints.addRelation(constraint);
		record.constraintId = constraint.id;
		this.setBinding(input.reference.referenceId, {
			constraint,
			record,
			targetSpline: input.targetSpline,
		});

		return {
			record,
			diagnostics: [],
		};
	}

	/**
	 * 拆除普通样条关系。
	 *
	 * @param input 拆除输入。
	 * @returns 诊断信息。
	 */
	public detach(input: {
		referenceId: string;
		targetSpline?: SketchSplineObject;
	}): ExecutionDiagnostic[] {
		const binding = this.getBinding(input.referenceId);
		if (binding) {
			binding.targetSpline.constraints.removeRelation(binding.record.constraintId);
			this.deleteBinding(input.referenceId);
			return [];
		}

		if (input.targetSpline) {
			const constraint = input.targetSpline.constraints.getRelations().find((candidate) => (
				candidate instanceof ReferenceDrivenSplineOnCurveConstraint
				&& candidate.referenceId === input.referenceId
			));
			if (constraint) {
				input.targetSpline.constraints.removeRelation(constraint.id);
			}
		}
		return [];
	}
}
